import { useEffect, useState } from 'react';
import { Box, Grid, Typography, Button, Alert, Divider } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ImageDropZone from './ImageDropZone';
import WalletPreviewDialog from './WalletPreviewDialog';
import NotificationTextField from './creation/tikeo/NotificationTextField';
import VersoBlocksPanel from './creation/tikeo/VersoBlocksPanel';
import { VersoBlock, readVersoBlocks } from './creation/tikeo/versoBlocks';
import useLatestRef from './creation/shared/useLatestRef';
import { CreationPayload, InitialPayload, readString } from './creation/shared/payloadReaders';
import { cardSx, outlinedButtonSx, sectionTitleSx } from './creation/shared/buttonStyles';

interface TikeoCreationSectionProps {
  initialPayload?: InitialPayload;
  onPayloadChange?: (payload: CreationPayload) => void;
}

const subtitleSx = { fontSize: '0.78rem', color: 'text.secondary', mb: 2, display: 'block' };

function TikeoCreationSection({ initialPayload, onPayloadChange }: TikeoCreationSectionProps) {
  const theme = useTheme();
  const [title, setTitle] = useState(() => readString(initialPayload, 'title'));
  const [message, setMessage] = useState(() => readString(initialPayload, 'message'));
  const [versoBlocks, setVersoBlocks] = useState<VersoBlock[]>(() => readVersoBlocks(initialPayload));
  const [rectoPreview, setRectoPreview] = useState<string>();
  const [previewOpen, setPreviewOpen] = useState(false);
  const onPayloadChangeRef = useLatestRef(onPayloadChange);

  useEffect(() => {
    onPayloadChangeRef.current?.({ type: 'wallet', title, message, versoBlocks });
  }, [title, message, versoBlocks, onPayloadChangeRef]);

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
        <Button variant="outlined" size="small" startIcon={<VisibilityIcon fontSize="small" />} onClick={() => setPreviewOpen(true)} sx={outlinedButtonSx(theme.palette.primary.main)}>
          Prévisualiser
        </Button>
      </Box>

      <Box sx={cardSx}>
        <Typography variant="subtitle2" sx={{ ...sectionTitleSx(theme.palette.primary.main), mb: 0.25 }}>
          Notification
        </Typography>
        <Typography variant="caption" sx={subtitleSx}>
          Paramétrez le message qui sera envoyé par notification push.
        </Typography>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <NotificationTextField
              label="Titre de la notification"
              hint="Le titre apparaît au verso de la carte Android."
              value={title}
              onChange={setTitle}
              placeholder="Titre de la notification..."
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <NotificationTextField
              label="Message de la notification"
              hint="Le message apparaît dans la notification du téléphone et dans le premier bloc au verso de la carte."
              value={message}
              onChange={setMessage}
              placeholder="Corps du message..."
              multiline
            />
            <Alert icon={<WarningAmberIcon fontSize="small" />} severity="warning" sx={{ mt: 1.5, borderRadius: 1.5, fontSize: '0.75rem', py: 0.5 }}>
              Ne pas intégrer de texte en HTML, ni lien, ni saut de ligne
            </Alert>
          </Grid>
        </Grid>
      </Box>

      <Box sx={cardSx}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.25 }}>
          <Typography variant="subtitle2" sx={sectionTitleSx(theme.palette.primary.main)}>
            Mettre à jour la carte
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            (facultatif)
          </Typography>
        </Box>
        <Typography variant="caption" sx={subtitleSx}>
          Lors de l&apos;envoi de la notification, le recto et le verso de la carte peuvent être modifiés le temps de la notification.
        </Typography>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5, color: 'primary.main', fontSize: '0.85rem' }}>
              Modifier le recto de la carte :
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, fontSize: '0.75rem' }}>
              Si vous ajoutez un visuel, celui-ci remplacera le visuel initial de la carte le temps de la notification.
            </Typography>
            <Typography variant="caption" fontWeight={600} sx={{ display: 'block', mb: 0.75, fontSize: '0.78rem' }}>
              Image mise en avant :
            </Typography>
            <ImageDropZone
              label="Recto"
              onFile={file => setRectoPreview(URL.createObjectURL(file))}
              preview={rectoPreview}
              hint="Taille de l'image : 750px × 246px · Formats acceptés : .jpeg ou .png · max 10Mo"
            />
          </Grid>

          <Grid size={{ xs: 12, md: 1 }}>
            <Divider orientation="vertical" sx={{ minHeight: 100, mx: 'auto' }} />
          </Grid>

          <Grid size={{ xs: 12, md: 5 }}>
            <VersoBlocksPanel blocks={versoBlocks} onChange={setVersoBlocks} />
          </Grid>
        </Grid>
      </Box>

      <WalletPreviewDialog open={previewOpen} onClose={() => setPreviewOpen(false)} data={{ title, message, versoBlocks, rectoImage: rectoPreview }} />
    </Box>
  );
}

export default TikeoCreationSection;
